import { PublicLayout } from '@/components/PublicLayout';
import { LogoMark, SectionEyebrow } from '@/components/ui';
import { LegalSection, LegalBlock, LegalLink } from './MentionsLegalesPage';
import { ShieldCheck, FileText } from 'lucide-react';

/**
 * Politique de confidentialité — conforme RGPD (UE) 2016/679.
 *
 * Contenu obligatoire (art. 12-14 RGPD) :
 *   - Identité du responsable de traitement + contact RGPD
 *   - Finalités + bases légales par traitement
 *   - Catégories de données collectées
 *   - Destinataires (interne + sous-traitants nommés)
 *   - Transferts hors UE (s'il y en a)
 *   - Durées de conservation
 *   - Droits (accès, rectification, effacement, opposition, limitation, portabilité)
 *   - Modalités d'exercice + droit de réclamation CNIL
 *   - Cookies (LCEN art. 32-II + ePrivacy 2002/58 + CNIL recom. 2020)
 *
 * Refonte R12 (mai 2026) :
 *   - Header glass avec LogoMark ShieldCheck accent tech + eyebrow + titre Geist
 *   - 10 sections numérotées avec LegalSection helper
 *   - Cards glass pour les blocs identité données collectées
 *   - Table glass dense pour finalités × bases légales × durées
 *   - Liens externes CNIL en LegalLink external
 *   - Footer version document mono
 */
export function PolitiqueConfidentialitePage() {
  return (
    <PublicLayout maxWidth="lg" centered={false}>
      <article className="space-y-6 py-4 animate-fade-in">
        {/* Header */}
        <header className="flex items-start gap-3 pb-4 border-b border-border">
          <LogoMark icon={ShieldCheck} accent="tech" size="lg" />
          <div className="flex-1">
            <SectionEyebrow as="div">Conformité RGPD · UE 2016/679</SectionEyebrow>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold tracking-r-tight text-ink">
              Politique de confidentialité
            </h1>
            <p className="mt-2 text-sm text-ink-subtle leading-relaxed">
              Notre engagement&nbsp;: vous expliquer simplement quelles données nous collectons,
              pourquoi, et comment vous gardez le contrôle.
            </p>
          </div>
        </header>

        {/* ============ 1. Responsable ============ */}
        <LegalSection number="1" title="Qui est responsable du traitement ?">
          <p className="text-sm text-ink-muted leading-relaxed">
            Le responsable de traitement est <strong className="text-ink">votre commune</strong>{' '}
            (celle que vous avez sélectionnée à l'inscription). Vision Pro Collectivité agit en
            qualité de <strong className="text-ink">sous-traitant</strong> au sens de l'article 28 du RGPD, dans le
            cadre d'un contrat de prestation de service conclu avec votre collectivité.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            En tant qu'organisme public, votre commune a désigné son propre délégué à la
            protection des données (art. 37 du RGPD)&nbsp;: c'est votre interlocuteur pour toute
            question sur le traitement de vos données. Ses coordonnées sont publiées sur le site
            officiel de la collectivité. Pour les questions techniques relevant de l'éditeur,
            vous pouvez écrire à{' '}
            <LegalLink href="mailto:contact@oneforall-distribution.fr">contact@oneforall-distribution.fr</LegalLink>.
          </p>
        </LegalSection>

        {/* ============ 2. Données collectées ============ */}
        <LegalSection number="2" title="Quelles données collectons-nous ?">
          <p className="text-sm text-ink-muted leading-relaxed">
            Principe de minimisation&nbsp;: nous ne collectons que les données strictement
            nécessaires à la fourniture du Service.
          </p>
          <LegalBlock>
            <div className="space-y-3">
              <div>
                <strong className="text-ink">Données d'identité&nbsp;:</strong> prénom, nom,
                adresse email, téléphone (optionnel).
              </div>
              <div>
                <strong className="text-ink">Données d'authentification&nbsp;:</strong> mot de passe
                (haché bcrypt 12 rounds, jamais en clair), 2FA TOTP (si activée).
              </div>
              <div>
                <strong className="text-ink">Données d'usage&nbsp;:</strong> date de création du
                compte, date de dernière connexion, adresse IP au moment de la connexion (audit
                sécurité).
              </div>
              <div>
                <strong className="text-ink">Données métier&nbsp;:</strong> contenu des
                signalements que vous déposez (sujet, description, photos, géolocalisation si vous
                l'autorisez), historique de traitement de vos demandes.
              </div>
              <div>
                <strong className="text-ink">Préférences&nbsp;:</strong> mode d'affichage
                (clair/sombre), opt-in/opt-out notifications par email.
              </div>
            </div>
          </LegalBlock>
        </LegalSection>

        {/* ============ 3. Finalités + bases légales — Table glass ============ */}
        <LegalSection number="3" title="Pourquoi ? (finalités et bases légales)">
          <p className="text-sm text-ink-muted leading-relaxed">
            Chaque traitement a un but identifié et une base juridique précise (art. 6 RGPD).
          </p>
          <div className="overflow-x-auto rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass shadow-soft">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-surface/40 backdrop-blur-glass">
                <tr className="text-left">
                  <th className="px-3 py-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Finalité</th>
                  <th className="px-3 py-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Base légale</th>
                  <th className="px-3 py-2 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Durée</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border last:border-b-0 transition-colors hover:bg-surface/60">
                  <td className="px-3 py-3 text-ink">Gestion du compte (création, connexion, profil)</td>
                  <td className="px-3 py-3 text-ink-muted">Exécution du contrat (art. 6.1.b)</td>
                  <td className="px-3 py-3 text-ink-muted">3 ans après dernière connexion, puis suppression</td>
                </tr>
                <tr className="border-b border-border transition-colors hover:bg-surface/60">
                  <td className="px-3 py-3 text-ink">Gestion des signalements (création, suivi, communication)</td>
                  <td className="px-3 py-3 text-ink-muted">Mission d'intérêt public (art. 6.1.e)</td>
                  <td className="px-3 py-3 text-ink-muted">3 ans à compter de la résolution</td>
                </tr>
                <tr className="border-b border-border transition-colors hover:bg-surface/60">
                  <td className="px-3 py-3 text-ink">Notifications par email</td>
                  <td className="px-3 py-3 text-ink-muted">Exécution du contrat (art. 6.1.b)</td>
                  <td className="px-3 py-3 text-ink-muted">Tant que le compte est actif</td>
                </tr>
                <tr className="border-b border-border transition-colors hover:bg-surface/60">
                  <td className="px-3 py-3 text-ink">Sécurité, audit, prévention de la fraude</td>
                  <td className="px-3 py-3 text-ink-muted">Intérêt légitime (art. 6.1.f)</td>
                  <td className="px-3 py-3 text-ink-muted">1 an (journal d'audit)</td>
                </tr>
                <tr className="transition-colors hover:bg-surface/60">
                  <td className="px-3 py-3 text-ink">Conservation des preuves d'acceptation CGU / politique</td>
                  <td className="px-3 py-3 text-ink-muted">Obligation légale (art. 6.1.c) + RGPD art. 7</td>
                  <td className="px-3 py-3 text-ink-muted">Durée de vie du compte + 3 ans</td>
                </tr>
              </tbody>
            </table>
          </div>
        </LegalSection>

        {/* ============ 4. Destinataires / sous-traitants ============ */}
        <LegalSection number="4" title="Qui voit vos données ?">
          <p className="text-sm text-ink-muted leading-relaxed">
            Vos données sont accessibles uniquement&nbsp;:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>
              Aux <strong className="text-ink">agents et élus de votre commune</strong>{' '}
              habilités, dans la stricte limite de leurs missions
              (signalements, gestion administrative)&nbsp;;
            </li>
            <li>
              À <strong className="text-ink">l'éditeur du Service</strong> (One For All Distribution) pour la
              maintenance technique, le support et la sécurité, avec un accès tracé et limité au
              strict nécessaire&nbsp;;
            </li>
            <li>
              À nos <strong className="text-ink">sous-traitants techniques</strong>, exclusivement
              pour fournir l'infrastructure.
            </li>
          </ul>
          <LegalBlock>
            <div>
              <strong className="text-ink">Sous-traitants RGPD</strong> (liste à jour)&nbsp;:
            </div>
            <ul className="mt-2 space-y-1.5 list-disc pl-5 text-ink-muted marker:text-ink-subtle">
              <li>
                <strong className="text-ink">Render Services, Inc.</strong> — hébergement
                applicatif + base de données + cache Redis (région Frankfurt, DE). DPA RGPD signé.
              </li>
              <li>
                <strong className="text-ink">Cloudflare R2</strong> — stockage des médias
                (pièces jointes, documents) et des sauvegardes chiffrées. Serveurs en UE. DPA RGPD signé.
              </li>
              <li>
                <strong className="text-ink">IONOS</strong> — envoi des emails
                transactionnels (vérification d'adresse, réinitialisation de mot de passe,
                notifications de signalements). Serveurs en UE. DPA RGPD signé.
              </li>
              <li>
                <strong className="text-ink">Yousign (Youtrust SAS)</strong> — signature
                électronique eIDAS des actes d'urbanisme. Établissement principal en France. DPA RGPD signé.
              </li>
              <li>
                <strong className="text-ink">Google (Firebase Cloud Messaging)</strong> — passerelle
                technique de distribution des notifications push web (protocole Web&nbsp;Push standard). Aucune
                donnée d'administré n'est stockée&nbsp;; seul un message de notification transite. Clauses
                contractuelles types Google Cloud signées.
              </li>
              <li>
                <strong className="text-ink">Anthropic</strong> — lecture automatisée (OCR) des
                factures de véhicules du parc municipal via l'API Claude. Traitement facultatif (un mode
                sans IA existe), ne portant que sur le contenu d'une facture de véhicule (aucune donnée
                d'administré). Hébergement aux États-Unis, encadré par clauses contractuelles types (DPA signé).
              </li>
              <li>
                <strong className="text-ink">FranceConnect / ProConnect</strong> (DINUM) — fédération
                d'identité pour la connexion des administrés et des agents. Service de l'État, hébergé en France.
                Habilitation administrative en vigueur.
              </li>
              <li>
                <strong className="text-ink">API Particulier</strong> (DINUM) — récupération du quotient
                familial CAF/MSA pour la facturation des services scolaires et périscolaires. Service de l'État,
                hébergé en France. Habilitation administrative en vigueur.
              </li>
              <li>
                <strong className="text-ink">PayFiP / DGFiP</strong> — paiement en ligne via la régie
                publique. Aucune donnée bancaire n'est stockée par l'éditeur. Service de l'État, hébergé en France.
              </li>
              <li>
                <strong className="text-ink">Functional Software, Inc. (Sentry)</strong> — télémétrie
                d'erreurs applicatives (surveillance technique du service). Région de traitement UE
                (Francfort). Données minimisées par configuration&nbsp;: pas d'adresse IP, pas de cookies,
                pas de corps de requêtes (aucune donnée d'administré)&nbsp;; conservation 90&nbsp;jours.
                Éditeur établi aux États-Unis&nbsp;: DPA signé incluant le Data Privacy Framework et les
                Clauses Contractuelles Types.
              </li>
            </ul>
          </LegalBlock>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">Transferts hors UE&nbsp;:</strong> un seul traitement peut
            donner lieu à un transfert hors Union européenne, l'<strong className="text-ink">OCR des
            factures de véhicules</strong> (API Anthropic, États-Unis). Ce traitement est facultatif et ne
            porte sur aucune donnée d'administré. Il est encadré par les <strong className="text-ink">Clauses
            Contractuelles Types</strong> de la Commission européenne (décision 2021/914). Tous les autres
            traitements sont réalisés dans l'Union européenne ou en France.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">Pas de vente, pas de cession.</strong> Vos données ne
            sont jamais vendues, louées ou transmises à des tiers à des fins commerciales.
          </p>
        </LegalSection>

        {/* ============ 5. Vos droits ============ */}
        <LegalSection number="5" title="Quels sont vos droits ?">
          <p className="text-sm text-ink-muted leading-relaxed">
            Le RGPD vous garantit plusieurs droits sur vos données. Vous pouvez les exercer à
            tout moment, gratuitement.
          </p>
          <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>
              <strong className="text-ink">Accès</strong> (art. 15)&nbsp;: obtenir une copie de
              vos données et savoir comment elles sont traitées&nbsp;;
            </li>
            <li>
              <strong className="text-ink">Rectification</strong> (art. 16)&nbsp;: corriger une
              donnée inexacte&nbsp;;
            </li>
            <li>
              <strong className="text-ink">Effacement</strong> (art. 17)&nbsp;: supprimer votre
              compte et vos données (sauf obligations légales de conservation)&nbsp;;
            </li>
            <li>
              <strong className="text-ink">Limitation</strong> (art. 18)&nbsp;: geler le
              traitement en cas de contestation&nbsp;;
            </li>
            <li>
              <strong className="text-ink">Portabilité</strong> (art. 20)&nbsp;: recevoir vos
              données dans un format structuré (JSON) pour les transmettre à un autre
              service&nbsp;;
            </li>
            <li>
              <strong className="text-ink">Opposition</strong> (art. 21)&nbsp;: refuser un
              traitement basé sur l'intérêt légitime&nbsp;;
            </li>
            <li>
              <strong className="text-ink">Définir le sort</strong> de vos données après votre
              décès (directives anticipées, loi Informatique &amp; Libertés art. 85).
            </li>
          </ul>
        </LegalSection>

        {/* ============ 6. Comment exercer ============ */}
        <LegalSection number="6" title="Comment exercer vos droits ?">
          <p className="text-sm text-ink-muted leading-relaxed">
            Trois canaux à votre disposition&nbsp;:
          </p>
          <ol className="list-decimal pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>
              <strong className="text-ink">Depuis votre compte</strong> (le plus simple)&nbsp;:
              modifier vos infos depuis{' '}
              <code className="rounded-r-md bg-surface/60 backdrop-blur-glass px-1.5 py-0.5 font-mono text-xs">/administre/compte</code>,
              supprimer votre compte en un clic.
            </li>
            <li>
              <strong className="text-ink">Par email</strong> auprès du DPO de votre
              collectivité, dont les coordonnées sont publiées sur le site officiel de la commune.
              Réponse sous 1 mois (extensible 2 mois si demande complexe, art. 12.3 RGPD).
              À défaut, vous pouvez écrire à{' '}
              <LegalLink href="mailto:contact@oneforall-distribution.fr">contact@oneforall-distribution.fr</LegalLink>,
              qui transmettra votre demande à la collectivité responsable du traitement.
            </li>
            <li>
              <strong className="text-ink">Auprès de la CNIL</strong> si vous estimez que vos
              droits ne sont pas respectés&nbsp;:{' '}
              <LegalLink href="https://www.cnil.fr/fr/plaintes" external>www.cnil.fr/fr/plaintes</LegalLink>.
            </li>
          </ol>
        </LegalSection>

        {/* ============ 7. Sécurité ============ */}
        <LegalSection number="7" title="Sécurité">
          <p className="text-sm text-ink-muted leading-relaxed">
            Nous mettons en œuvre les mesures techniques et organisationnelles appropriées (art.
            32 RGPD) pour garantir la sécurité de vos données&nbsp;:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>Chiffrement TLS 1.2+ sur toutes les communications réseau ;</li>
            <li>Mots de passe hachés avec bcrypt (12 rounds), jamais stockés en clair ;</li>
            <li>
              Vérification systématique contre les bases de mots de passe divulgués (HaveIBeenPwned)
              au moment du choix d'un mot de passe ;
            </li>
            <li>
              Verrouillage temporaire automatique après 5 tentatives échouées (politique CNIL 2024) ;
            </li>
            <li>Double facteur (2FA TOTP) disponible et fortement recommandé ;</li>
            <li>
              Journal d'audit immuable sur les actions sensibles (connexion, modification de
              compte, accès aux signalements par les agents) ;
            </li>
            <li>Sauvegardes quotidiennes chiffrées avec rétention 30 jours.</li>
          </ul>
        </LegalSection>

        {/* ============ 8. Cookies ============ */}
        <LegalSection number="8" title="Cookies et traceurs">
          <p className="text-sm text-ink-muted leading-relaxed">
            Vision Pro Collectivité utilise <strong className="text-ink">uniquement des cookies
            strictement nécessaires</strong> au fonctionnement du Service (CNIL Recommandation 2020)&nbsp;:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>
              Cookie de session d'authentification (refresh token,{' '}
              <code className="rounded-r-md bg-surface/60 backdrop-blur-glass px-1.5 py-0.5 font-mono text-xs">HttpOnly + Secure + SameSite=Strict</code>),
              durée 15 jours ;
            </li>
            <li>
              Préférence d'affichage (clair/sombre) stockée dans{' '}
              <code className="rounded-r-md bg-surface/60 backdrop-blur-glass px-1.5 py-0.5 font-mono text-xs">localStorage</code>{' '}
              du navigateur (non transmise au serveur).
            </li>
          </ul>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">Aucun cookie de tracking ou d'analyse</strong> n'est
            déposé. Aucun pixel publicitaire. Pas de Google Analytics, pas de réseau social
            tiers. La bannière cookies n'est donc pas requise — votre consentement n'est sollicité
            que pour les fonctionnalités optionnelles que vous activez explicitement.
          </p>
        </LegalSection>

        {/* ============ 9. Mineurs ============ */}
        <LegalSection number="9" title="Mineurs">
          <p className="text-sm text-ink-muted leading-relaxed">
            La création autonome d'un compte est réservée aux personnes <strong className="text-ink">majeures</strong>{' '}
            (article 8 RGPD + loi Informatique &amp; Libertés art. 45). Les mineurs peuvent
            utiliser le Service via le compte d'un parent ou représentant légal. Aucune donnée
            de mineur de moins de 15 ans n'est traitée sans consentement parental préalable.
          </p>
        </LegalSection>

        {/* ============ 10. Modifications ============ */}
        <LegalSection number="10" title="Modifications de la politique">
          <p className="text-sm text-ink-muted leading-relaxed">
            Cette politique peut être amendée pour s'adapter à des évolutions légales ou
            techniques. La version courante est référencée dynamiquement via{' '}
            <code className="rounded-r-md bg-surface/60 backdrop-blur-glass px-1.5 py-0.5 font-mono text-xs">GET /api/legal/versions</code>.
            En cas de modification substantielle, vous serez informé par email et invité à
            ré-accepter la nouvelle version lors de votre prochaine connexion.
          </p>
        </LegalSection>

        <footer className="pt-4 border-t border-border text-xs text-ink-subtle">
          <FileText className="mr-1 inline h-3.5 w-3.5 align-text-bottom" aria-hidden="true" />
          Version&nbsp;: <strong className="text-ink">1.0</strong> · Dernière mise à jour&nbsp;:{' '}
          1.0.
        </footer>
      </article>
    </PublicLayout>
  );
}
